import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { formatCurrency } from '@/lib/format';

const CHART_TOOLTIP_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 13,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
};

const COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  '#ec4899',
  '#06b6d4',
  '#f97316',
  '#6366f1',
  '#14b8a6',
];

export function DailyCostChart({
  data,
  selectedDate,
  onDateClick,
}: {
  data: { date: string; amount: number }[];
  selectedDate?: string | null;
  onDateClick?: (date: string | null) => void;
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
          Daily Spend
        </h3>
        {selectedDate && onDateClick && (
          <button
            onClick={() => onDateClick(null)}
            className="text-primary text-xs hover:underline"
          >
            Clear selection
          </button>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            onClick={(e) => {
              if (e?.activePayload?.[0]?.payload?.date && onDateClick) {
                const clickedDate = e.activePayload[0].payload.date;
                onDateClick(clickedDate === selectedDate ? null : clickedDate);
              }
            }}
            style={{ cursor: onDateClick ? 'pointer' : undefined }}
          >
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => {
                const date = new Date(d);
                return date.getDate() === 1
                  ? date.toLocaleDateString('en-US', { month: 'short' })
                  : date.getDate().toString();
              }}
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(v) => `$${v.toFixed(0)}`}
              fontSize={12}
              tickLine={false}
              axisLine={false}
              width={45}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(value: number) => [formatCurrency(value), 'Cost']}
              labelFormatter={(label) =>
                new Date(label).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              }
            />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="var(--chart-1)"
              fill="var(--chart-1)"
              fillOpacity={0.1}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {!selectedDate && (
        <p className="text-muted-foreground mt-2 text-center text-xs">
          Click a day to see its service breakdown
        </p>
      )}
    </div>
  );
}

export function ServiceCostChart({
  data,
  selectedDate,
}: {
  data: { service: string; amount: number }[];
  selectedDate?: string | null;
}) {
  const title = selectedDate
    ? `Cost by Service — ${new Date(selectedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : 'Cost by Service (Current Month)';

  return (
    <div>
      <h3 className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wider">
        {title}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            <XAxis
              type="number"
              tickFormatter={(v) => `$${v.toFixed(0)}`}
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="category"
              dataKey="service"
              width={180}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickFormatter={(s) => s.replace('Amazon ', '').replace('AWS ', '')}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(value: number) => [formatCurrency(value), 'Cost']}
            />
            <Bar dataKey="amount">
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
